import React, { useEffect, useMemo, useRef, useState } from "react";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import { PageWrapper } from "../../../transaction-certificates/tc-dashboard/cb-landingpage";
import { Box, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { CustomSelect } from "../../../../shared/components/inputfields/form-elements";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import * as Yup from "yup";
import CustomTable from "../../../../shared/components/table/custom-table";
import { MRT_ColumnDef } from "material-react-table";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../shared/app-configs/sc-app-config";
import { OptionType } from "../../../../shared/components/form-builder/form-config.model";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { useFormActions } from "../../../../shared/hooks/form-builder.hooks";
import { StatusCell } from "../../../transaction-certificates/tc-dashboard/cbLandingPageTable.config";
import { RejectReasonCell } from "./scTableConfig";
import { coNameDetailsService } from "../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { Utills } from "../../../../shared/app-configs/utilit";
import moment from "moment";
import { useScStore } from "../../../../store/scStandard.store";
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RateReviewIcon from '@mui/icons-material/RateReview';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ViewerModalPopup from "../../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../../shared/components/preview/pdf-viewer";
import useNotification from "../../../../shared/hooks/useNotification";
import { StringValidation } from "../../../../shared/utils/custom-validation.utils";

interface NavTCNoProps {
  cell: {
    getValue: () => string;
    row: {
      original: {
        submissionStatus: string;
        scId: string;
        parentSubmissionStatus: string;
        scNo?: string;
        scVersionNo?: string;
        [key: string]: any;
      };
    };
  },
  type: string;
}
const NavTCNo: React.FC<NavTCNoProps> = ({ cell }) => {
  const navigate = useNavigate();

  // Extracting necessary values
  const tcNo = cell.getValue(); // Transaction Certificate Number
  const { submissionStatus, scId } = cell.row.original;

  // Click handler to navigate based on submissionStatus
  const handleNavigation = () => {
    if (submissionStatus === "Submitted" || submissionStatus === "Approved") {
      navigate(`/sc/submited-sc/scope-certificate-details/${scId}`);
    }
    else if (submissionStatus === "Rejected") {
      navigate(`/sc/rejected-sc/scope-certificate-details/${scId}`);
    }
    else {
      navigate(`/sc/sc-details/scope-certificate-details/${scId}`);
    }
  };

  return (
    <Typography
      onClick={handleNavigation}
      variant="body2"
      sx={{
        cursor: "pointer",
        color: "primary.main",
        textDecoration: "underline",
        "&:hover": {
          color: "primary.dark", // Slightly darker color on hover
        },
        "&:active": {
          textDecoration: "none", // Remove underline when clicked
        },
      }}
    >
      {tcNo}
    </Typography>
  );
};
const SuppliersDashboard = () => {
  const { executeGet, setLoadingState, loading } = useApi();
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const [singleValue, setSingleValue] = React.useState("dTrackit");
  const links = [{ label: "323", value: "2332" }];
  const { setNewScRequested, cbLicensingCode } = useTransactionCertificateStore();
  const { resetAllDatas } = useFormActions("")
  const [isResetEnabled, setResetEnable] = useState(false);
  const [isSearchDisabled, setSearchDisabled] = useState(true);
  const { setEditSCInfo: updateScInfo } = useScStore();
  const isCbUser = cbLicensingCode.startsWith("CB");

  const { notify } = useNotification();

  const onNewRequestTCClick = () => {
    // Navigate to request TC page
    updateScInfo({});
    setNewScRequested(true)
    navigate("/sc/create-sc/scope-certificate-details");
  };
  const [dTrackitdata, setDTrackitdata] = useState<Array<any>>([]);
  const [historicdata, setHistoricdata] = useState<Array<any>>([]);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [pagination, setPagination] = useState(
    { pageIndex: 0, pageSize: 20 }
  );

  // Compute rowCount based on hasMorePages for pagination
  const computedRowCount = hasMorePages
    ? (pagination.pageIndex + 2) * pagination.pageSize // Fake count to enable Next button
    : (pagination.pageIndex + 1) * pagination.pageSize; // Exact count on last page
  const fetchDtraickitDetails = async (params?: any) => {
    const result = await executeGet(API_ROUTES.GET_SC_DASHBOARD, {
      scCbLicensingCode: cbLicensingCode,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      scNo: params?.scNo,
      ...params
    });
    Utills.cl(result?.data?.response)
    if (result?.data?.response?.length > 0) {
      setDTrackitdata(result?.data.response);
      setHasMorePages(result?.data?.has_more ?? false);
    }
    else {
      console.error("Result is undefined or missing required data.");
    }
  };

  const fetchHistoricalDetails = async (params?: any) => {
    const result = await executeGet(API_ROUTES.GET_SC_HISTORICAL_DASHBOARD, {
      scCbLicensingCode: cbLicensingCode,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      scNo: params?.scNo,
      ...params
    });
    if (result?.data?.response) {
      setHistoricdata(result.data.response);
      setHasMorePages(result.data?.has_more ?? false);
    } else {
      console.error("Result is undefined or missing required data.");
    }
  };
  useEffect(() => {
    if (singleValue === 'dTrackit')
      fetchDtraickitDetails();
    else
      fetchHistoricalDetails()
  }, [pagination.pageIndex, pagination.pageSize, singleValue]);

  useEffect(() => {
    resetAllDatas()
  }, []);
  const sourceOptions: OptionType[] = [
    { label: "SC (dTrackit)", value: "dTrackit" },
    { label: "SC (historical)", value: "CB-ITS" },
  ];

  const EditSC: React.FC<NavTCNoProps> = ({ cell, type }) => {
    const { scId, submissionStatus, parentSubmissionStatus } = cell.row.original;
    const navigate = useNavigate();
    const { setEditSCInfo } = useScStore();
    const { executePost } = useApi();
    const [previewOpen, setPreviewOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

    // Click handler to navigate based on tcMetadataStatus
    const handleNavigation = () => {
      if (type === 'suppliers') {
        navigate(`/sc/submited-sc/scope-certificate-details/${scId}`);
      }
      else {
        if (
          parentSubmissionStatus === "Submitted" ||
          parentSubmissionStatus === "Success" ||
          parentSubmissionStatus === "Rejected" ||
          submissionStatus === "Amending"
        ) {
          setEditSCInfo({ scId: scId, isParentStatus: true });
        }
        else {
          setEditSCInfo({ scId: scId, isParentStatus: false });
        }
        navigate(`/sc/sc-details/scope-certificate-details/${scId}`);
      }
    }
    const handleEdit = async (row: any) => {
      try {

        const payload = {
          ...row,
          scCbLicensingCode: cbLicensingCode
        }

        const url = `${API_ROUTES.EDIT_SC_DETAILS}?` +
          `scId=${scId}&` +
          `scCbLicensingCode=${encodeURIComponent(cbLicensingCode)}`;

        const result: any = await executePost(url, payload);

        if (result.status === 200) {
          // result.data.tcId
          if (
            submissionStatus === "Submitted" ||
            submissionStatus === "Success" ||
            submissionStatus === "Rejected"
          ) {
            setEditSCInfo({
              scId: result.data.scId,
              isParentStatus: true
            });
          }
          else {
            setEditSCInfo({});
          }
          navigate(`/sc/sc-details/scope-certificate-details/${result.data.scId}`);
        }
      }
      catch (e) {
        console.error(`Exception Occured in Editing SC ${e}`)
      }
    }

    const handleView = async (row: any) => {
      try {
        setPreviewOpen(true);
      }
      catch (e) {
        console.error(`Exception Occured in Viewing SC ${e}`)
      }
    }

    const handleUploadPdf = () => {
      fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.type !== 'application/pdf') {
        notify('Only PDF files are allowed', 'error');
        return;
      }

      // Validate file name format: SC_CBCode_scNo_YYYYMMDD.pdf
      const fileName = file.name;
      const fileNamePattern = /^SC_([A-Z0-9]{3})_(.+)_(\d{8})\.pdf$/i;
      const match = fileName.match(fileNamePattern);

      if (!match) {
        notify('Invalid file name format. Expected format: SC_CBCode_scNo_YYYYMMDD.pdf', 'error');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const [, cbCode, scNo, dateStr] = match;

      if (cbCode.length !== 3) {
        notify('CB code must be exactly 3 characters long', 'error');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const year = parseInt(dateStr.substring(0, 4));
      const month = parseInt(dateStr.substring(4, 6));
      const day = parseInt(dateStr.substring(6, 8));

      if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
        notify('Invalid date in file name. Date must be in YYYYMMDD format', 'error');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const dateObj = new Date(year, month - 1, day);
      if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
        notify('Invalid date in file name. Please provide a valid date', 'error');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sc_id', scId);
        formData.append('sc_no', cell.row.original.scNo || '');
        formData.append('cb_id', cbLicensingCode);
        formData.append('status', submissionStatus);
        formData.append('sc_version_no', cell.row.original.scVersionNo || '');

        const result = await executePost(
          API_ROUTES.SC_UPLOAD_PDF,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (result?.status === 200) {
          notify('PDF uploaded successfully', 'success');
        }
      } catch (error) {
        console.error('Error uploading PDF:', error);
        notify('Error uploading PDF', 'error');
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    return (
      <>
        <Box display="flex" alignItems="center">
          {["Success"].includes(submissionStatus) && (
            <>
              <Tooltip title="Upload PDF">
                <IconButton onClick={handleUploadPdf} color="primary">
                  <UploadFileIcon sx={{ fontSize: 20, color: '#2C4748' }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ marginX: 1 }} />
            </>
          )}
          {["Success", "Rejected"].includes(submissionStatus) && (
            <>
              <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(cell.row.original)} color="primary">
                  <EditIcon sx={{ fontSize: 20, color: '#353C52' }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ marginX: 1 }} />
            </>
          )}
          {["Amending", "Draft"].includes(submissionStatus) && isCbUser && (
            <>
              <Tooltip title="Continue Submission">
                <IconButton onClick={() => handleNavigation()} color="primary">
                  <RateReviewIcon sx={{ fontSize: 20, color: '#2C4748' }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ marginX: 1 }} />
            </>
          )}
          {submissionStatus !== "Draft" && (
            <Tooltip title="View">
              <IconButton onClick={() => handleView(cell.row.original)} color="primary">
                <VisibilityIcon sx={{ fontSize: 20, color: '#F0CD7F' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".pdf"
          onChange={handleFileChange}
        />
        {scId && (
          <ViewerModalPopup
            open={previewOpen}
            onClose={togglePreviewDrawer}
            title="Preview"
          >
            <div style={{ width: '100%', height: '100vh' }}>
              <PdfViewer
                apiUrl={API_ROUTES.SC_PREVIEW}
                params={{ scId: scId }}
              />
            </div>
          </ViewerModalPopup>
        )}
      </>
    );
  };

  const standardDetails = [
    { label: "Organic Content Standard (OCS)", value: "OCS" },
    { label: "Responsible Down Standard (RDS)", value: "RDS" },
    { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
    { label: "Global Recycled Standard (GRS)", value: "GRS" },
    { label: "Responsible Wool Standard (RWS)", value: "RWS" },
    { label: "Content Claim Standard (CCS)", value: "CCS" },
    { label: "Recycled Claim Standard (RCS)", value: "RCS" },
    { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
    { label: "Sustainable Fiber Alliance (SFA)", value: "SFA" },
    { label: "Global Organic Textile Standard (GOTS)", value: "GOTS" }
  ]

  const dtrackitcolumns = [
    {
      accessorKey: "Action",
      header: "",
      minSize: 40,
      maxSize: 85,
      Cell: EditSC,
    },
    {
      accessorKey: "scId",
      header: "id",
      size: 0, // Force size to 0
      enableHiding: true, // Allow programmatic hiding
      muiTableHeadCellProps: {
        style: { display: "none" }, // Hide the header cell
      },
      muiTableBodyCellProps: {
        style: { display: "none" }, // Hide the body cell
      },
    },
    { accessorKey: "scNo", header: "SC No.", minSize: 60, maxSize: 150 },
    {
      accessorKey: "scVersionNo",
      header: "SC Version No.",
      minSize: 60,
      maxSize: 150,
    },

    {
      accessorKey: "certifiedOrganizationName",
      header: "Certified Organization Name",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "scStandard",
      header: "SC Standard",
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: any) => {
        let selectedValues = cell.getValue() || []; // Get the array of selected values
        //(selectedValues, "43217809478213");
        selectedValues = selectedValues.map((val: any) => {
          return standardDetails.find(det => det.value === val)?.label
        })
        return (
          <div>
            {Array.isArray(selectedValues) && selectedValues.length > 0
              ? selectedValues.join(", ") // Convert array to comma-separated string
              : ""} {/* Fallback if no value is selected */}
          </div>
        );
      },
    },
    {
      accessorKey: "scStatus",
      header: "SC Status",
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: any) => {
        const statusMapping: Record<string, string> = {
          V: "Valid",
          E: "Expired",
          S: "Suspended",
          W: "Withdrawn",
        };

        // Extract the status value from the row data
        const statusValue = cell.getValue();

        // Map the status value to the corresponding label
        const label = statusMapping[statusValue] || statusValue;

        return <span>{label}</span>;
      }
    },
    {
      accessorKey: "scCreationDate",
      header: "SC Creation Date",
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: { cell: any }) => {
        const date = cell.getValue(); // Access the raw date value
        const formattedDate = moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD") // Format valid date
          : date; // Fallback to raw value if invalid
        return <span>{formattedDate}</span>;
      }
    },
    {
      accessorKey: "scDateOfIssue",
      header: "SC Date Of Issue",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "scValidUntil",
      header: "SC Valid Until",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "brandName",
      header: "Brand Name",
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: any) => {
        const selectedValues = cell.getValue() || []; // Get the array of selected values
        //(selectedValues, "43217809478213");

        return (
          <div>
            {Array.isArray(selectedValues) && selectedValues.length > 0
              ? selectedValues.join(", ") // Convert array to comma-separated string
              : ""} {/* Fallback if no value is selected */}
          </div>
        );
      },
    },

    {
      accessorKey: "submissionStatus",
      header: "Submission Status",
      minSize: 60,
      maxSize: 150,
      Cell: StatusCell
    },
    {
      accessorKey: "rejectionReason",
      header: "Reject Reason",
      minSize: 80,
      maxSize: 120,
      Cell: ({ row }: any) => <RejectReasonCell row={row} />,
    }
  ];
  const historicalColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "sc_no",
      header: "SC No.",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "version_number",
      header: "SC Version Number",
      minSize: 120,
      maxSize: 200,
    },
    {
      accessorKey: "co_name",
      header: "Certified Organization Name",
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: "sc_standard",
      header: "SC Standard",
      minSize: 150,
      maxSize: 300,
    },
    {
      accessorKey: "sc_status",
      header: "Status",
      minSize: 80,
      maxSize: 120,
      Cell: ({ cell }: any) => {
        const statusMapping: Record<string, string> = {
          V: "Valid",
          E: "Expired",
          S: "Suspended",
          W: "Withdrawn",
        };

        // Extract the status value from the row data
        const statusValue = cell.getValue();

        // Map the status value to the corresponding label
        const label = statusMapping[statusValue] || statusValue;

        return <span>{label}</span>;
      }
    },
    {
      accessorKey: "creation_date",
      header: "SC Creation Date",
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: "date_of_issue",
      header: "SC Date of Issue",
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: "sc_valid_until",
      header: "SC Valid Until",
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: "brand_name",
      header: "Brand Name",
      minSize: 100,
      maxSize: 200,
    },
  ];
  const handleCertifiedOrganizationNameChange = (value: any) => {
    formRef.current.setFieldValue("coName", value == null ? "" : value.value);
  };
  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        name: type == "query" ? value : "",
        offset: offset,
        limit: 10,
        scCbLicensingCode: cbLicensingCode
      };
      // Fetch TE Details using the getTEDetails function
      const response = singleValue === "dTrackit" ? await coNameDetailsService.getdtrackitCoNameDetails(queryParams)
        : await coNameDetailsService.gethistoricCoNameDetails(queryParams);

      const data = response.data.response.map((item: any) => ({
        label: item,
        value: item,
      }));


      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error; // Re-throw error to handle it in the calling code
    }
  };

  const handleSCNOChange = (value: any) => {
    formRef.current.setFieldValue("scNo", value == null ? "" : value.value);
  };

  const getSCNoforSearch = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        scNo: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
        scCbLicensingCode: cbLicensingCode
      };
      const response: any = await executeGet(API_ROUTES.GET_SC_NO, queryParams);
      const data = response.data.response.map((item: any) => ({
        label: item,
        value: item,
      }));
      // //alert(JSON.stringify(response.data))
      return data;
    }
    catch (e) {
      console.error(`Error in Fetching SCNo ${e}`);
    }
  }

  const formConfig = {
    pageName: "sc-landing-page",
    initialValues: {
      coName: "", // For multi-select
      startDate: null,
      endDate: null,
      source: singleValue,
    },
    validationSchema: Yup.object().shape({
      coName: Yup.string().nullable(),
      startDate: Yup.string().nullable(),
      endDate: Yup.string().nullable(),
      source: Yup.string().nullable(),
    }),
    sections: [
      {
        fields: [
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "search",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              size: "small",
              onChange: handleCertifiedOrganizationNameChange,
              displayKey: "label",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
            },
          },
          // Start Date Field
          {
            label: "Start Date (issue Date)",
            name: "startDate",
            type: "date",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            value: null, // Initial value as null for date
            props: {
              placeholder: "Select Start Date",
            },
          },
          // End Date Field
          {
            label: "End Date (issue Date)",
            name: "endDate",
            type: "date",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            value: null, // Initial value as null for date
            props: {
              placeholder: "Select End Date",
            },
          },
          {
            label: "SC No.",
            name: "scNo",
            type: "search",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: "Search SC No.",
              onChange: handleSCNOChange,
              displayKey: "label",
              onSearch: (query: string, offset: number) => {
                const response = getSCNoforSearch(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          // {
          //   label: "Source",
          //   name: "source",
          //   type: "select",
          //   gridSize: { xs: 12, md: 3, lg: 3 },
          //   required: false,
          //   props: {
          //     size: "small",
          //     options: sourceOptions,
          //     onChange: (event: any) => {
          //       setSingleValue(event.target.value);
          //     },
          //   },
          // },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      await handleSearchSubmit(values, 1);
    },
  };
  const handleSearchSubmit = async (values: any, isReseted?: number) => {
    try {
      if (isReseted === 1) {
        const isEmpty = (val: any) => StringValidation.isNullOrEmpty(val);
        const allFieldsEmpty = [values.coName, values.scNo, values.startDate, values.endDate].every(isEmpty);
        if (allFieldsEmpty) {
          notify("Please fill at least one field", "error");
          return;
        }
      }

      let params = {
        startDate: values.startDate,
        endDate: values.endDate,
        co_name: Array.isArray(values.coName) ? values.coName : [values.coName],
        scNo: values.scNo,
      };

      if (singleValue === 'dTrackit')
        fetchDtraickitDetails(params);
      else
        fetchHistoricalDetails(params);

      if (isReseted === 1) {
        setResetEnable(true)
      }
      else {
        setResetEnable(false)
      }

    } catch (error) {
      console.log("Error fetching data: ", error);
    }
  }
  const handleReset = async () => {
    formRef.current.resetForm();
    setResetEnable(false);

    let params = {
      startDate: null,
      endDate: null,
      co_name: [],
      scNo: null,
    };

    await handleSearchSubmit(params, 0);
  }

  // useEffect(() => {
  //   const values = formRef.current?.values;
  //   if (!values) return true;

  //   const { coName, startDate, endDate } = values;

  //   if (
  //     !coName || coName === "" ||
  //     !startDate ||
  //     !endDate
  //   ){

  //   }

  // },[formRef.current?.values])



  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  return (
    <>
      <PageWrapper>
        <Typography variant="h6" gutterBottom align="left" fontWeight={"600"}>
          Scope Certificate
        </Typography>
        <Box
          display="flex"
          justifyContent="flex-end" // Align items to the end of the container
          alignItems="center" // Vertically center the button and select
          width="100%" // Make the box span the entire width
          mb="12px"
          gap={2} // Add gap for spacing between elements
        >
          <StyledButton
            onClick={onNewRequestTCClick}
            variant="contained"
            color="warning"
            sx={{ height: "40px", mb: 2 }} // Ensure consistent button height
          >
            Create SC
          </StyledButton>

          {/* <Grid md={2} lg={2}>
            <CustomSelect
              size="small"
              // value={singleValue}
              options={links}
              placeholder="Login To"
              onChange={() => {
                // Utills.cl();
              }}
              sx={{ minWidth: "180px", height: "40px" }} // Ensure consistent select height
            />
          </Grid> */}
        </Box>
        <FormBuilder
          formConfig={formConfig}
          onSubmit={formConfig.handleSubmit}
          formRef={formRef}
        />
        {/* </Grid> */}

        <Grid
          item
          md={2}
          lg={2}
          display="flex"
          justifyContent={"flex-end"}
          alignItems="center"
        >
          <StyledButton onClick={handleSubmit}>Search</StyledButton>
          {isResetEnabled ?
            <StyledButton onClick={handleReset}>Reset</StyledButton>
            : null
          }
        </Grid>

        {/* </Grid> */}

        <CustomTable
          columns={singleValue === "dTrackit" ? dtrackitcolumns : historicalColumns}
          data={singleValue === "dTrackit" ? dTrackitdata : historicdata}
          title="TransactionData"
          enableTopToolbar={true}
          enableBottomToolbar={true}
          isLoadingTodos={loading}
          rowCount={computedRowCount}
          paginations={pagination} // Pass current pagination state
          setPaginations={setPagination}
        />
      </PageWrapper>
    </>
  );
};

export default SuppliersDashboard;
